import ContactsIcon from "@mui/icons-material/Contacts";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import Groups2Icon from "@mui/icons-material/Groups2";
import HandshakeIcon from "@mui/icons-material/Handshake";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CommentIcon from "@mui/icons-material/Comment";
import InfoIcon from '@mui/icons-material/Info';
import MoreTimeIcon from '@mui/icons-material/MoreTime';

import { type FC, useEffect, useState } from "react";
import { type Opportunity } from "@entities";
import { LtrNumberRatio } from "@shared/frontend/components/LtrNumberRatio";
import EmojiPeopleOutlinedIcon from "@mui/icons-material/EmojiPeopleOutlined";
import { Box, Button, CircularProgress, MenuItem, Select, Stack } from "@mui/material";
import { opportunityRepo, organizationManagerRepo } from "@shared/Repositories";
import { useCommunities } from "../../queries/communities.query";
import { useSkills } from "../../queries/skills.query";
import { EventDayDate } from "../event-day-date/EventDayDate";
import StringDisplay from './StringDisplay';
import {
  formatOpportunitySchedule,
  formatOpportunityTimeDescription,
  getOpportunityScheduleDate,
} from "@shared/helpers/format-opportunity-schedule";

import "./EventDetails.scss";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useAlert } from "../../contexts/AlertProvider";

interface EventDetailsProps {
  eventDetails: Opportunity | undefined;
  displayAll?: boolean;
  onOpenPartnerOrganization?: () => void;
  hideTitle?: boolean;
  tourAnchors?: boolean;
  /** Larger type and a longer description, for the call-center card. */
  spacious?: boolean;
  /** Show the manager already on the opportunity, without a lookup. */
  previewMode?: boolean;
}

type DetailRow = {
  key: string;
  content: string | JSX.Element | undefined;
  icon: JSX.Element;
  tourAnchor?: string;
};

export const EventDetails: FC<EventDetailsProps> = ({
  eventDetails,
  onOpenPartnerOrganization,
  spacious = false,
  previewMode = false,
  tourAnchors = false,
}) => {
  const { currOrganization } = useOrganization();
  const { communities = [] } = useCommunities();
  const { setAlertText, setSeverity } = useAlert();
  const [adminName, setAdminName] = useState("");
  const style = {
    color: "gray",
    fontSize: "1.3rem",
  };
  const { skillsAndVehicles } = useSkills();

  useEffect(() => {
    getAdminDetails();
  }, [EventDetails]);

  async function getAdminDetails() {
    const fallback = eventDetails?.manager?.name || "";
    if (previewMode) {
      setAdminName(fallback);
      return;
    }
    const manager = await organizationManagerRepo.findOne({
      where: { id: eventDetails?.manager?.id },
    });
    setAdminName(manager?.name || fallback);
  }

  const durationLabel = eventDetails?.isLongTerm
    ? "התנדבות ארוכת טווח"
    : "התנדבות חד-פעמית";

  const firstDetailsGroup: DetailRow[] = [
      {
        key: "schedule",
        tourAnchor: "cc-event-schedule",
        content: eventDetails ? (
          <>
            {formatOpportunitySchedule(eventDetails)}
            <Box
              component="span"
              sx={{ display: "block", fontWeight: 700, color: "text.primary" }}
            >
              {durationLabel}
            </Box>
          </>
        ) : (
          "-"
        ),
        icon: <InsertInvitationIcon style={style} />,
      },
      {
        key: "location",
        tourAnchor: "cc-event-location",
        content: eventDetails?.location,
        icon: <LocationOnIcon style={style} />,
      },
      {
        key: "manager",
        content: adminName,
        icon: <ContactsIcon style={style} />,
      },
      // {
      //   content: eventDetails?.progressStatus?.title,
      //   icon: <BeenhereOutlinedIcon style={style} />,
      // },
    ];

  if (eventDetails?.partnerOrganizationId && onOpenPartnerOrganization) {
    const partnerOrg = eventDetails.partnerOrganization;
    firstDetailsGroup.push({
      key: "partner",
      tourAnchor: "cc-event-partner",
      content: (
        <Button
          variant="text"
          size="small"
          onClick={onOpenPartnerOrganization}
          sx={{
            p: 0,
            minWidth: 0,
            textAlign: "start",
            textDecoration: "underline",
            color: "primary.main",
            fontSize: "inherit",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline", bgcolor: "transparent" },
          }}
          aria-label="פרטי ארגון שותף"
        >
          {partnerOrg?.name ?? "ארגון שותף"}
        </Button>
      ),
      icon: <CorporateFareIcon style={style} />,
    });
  }

  if (eventDetails?.associationRequested?.user)
    firstDetailsGroup.push({
      key: "requester",
      content:
        eventDetails.associationRequested.user.name +
        ", " +
        "0" +
        eventDetails.associationRequested.user.phoneNumber.slice(
          4,
          eventDetails.associationRequested.user.phoneNumber.length,
        ),
      icon: <EmojiPeopleOutlinedIcon style={style} />,
    });

  const secondDetailsGroup: DetailRow[] = [
    {
      key: "amount",
      tourAnchor: "cc-event-amount",
      content: (
        <LtrNumberRatio
          left={eventDetails?.participantsCount ?? 0}
          right={eventDetails?.amount ?? 0}
        />
      ),
      icon: <HandshakeIcon style={style} />,
    },
    {
      key: "skills",
      content: eventDetails?.requiredSkills?.length
        ? skillsAndVehicles?.skills
          .filter((skill) =>
            eventDetails?.requiredSkills?.some(
              (requiredSkill) => requiredSkill.skillId === skill.id,
            ),
          )
          ?.map((skill) => skill.name)
          .splice(0, 2)
          .join(" , ")
        : "-",
      icon: <VolunteerActivismIcon style={style} />,
    },
    {
      key: "teams",
      content: eventDetails?.isPublic
        ? "ציבורית"
        : (communities || [])
          .filter((team) =>
            eventDetails?.teams?.some(
              (eventTeam) => eventTeam.teamId === team.id,
            ),
          )
          .map((team) => team.name)
          .join(" , "),
      icon: <Groups2Icon style={style} />,
    },
  ];

  const ThirdDetailsGroup: DetailRow[] = [
    {
      key: "description",
      tourAnchor: "cc-event-description",
      content: (
        <StringDisplay
          text={eventDetails?.description || "-"}
          previewChars={spacious ? 280 : 50}
        />
      ),
      icon: <InfoIcon style={style} />,
    },
    {
      key: "comments",
      content: (
        <StringDisplay
          text={eventDetails?.comments || "-"}
          previewChars={spacious ? 280 : 50}
        />
      ),
      icon: <CommentIcon style={style} />,
    },
    {
      key: "created",
      content:
        "תאריך יצירה: " +
        (eventDetails?.createdAt?.toLocaleDateString("he-IL") ?? "-"),
      icon: <MoreTimeIcon style={style} />,
    }
  ];

  if (!eventDetails)
    return (
      <Stack
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Stack>
    );

  return (
    <div className={`event-details-container${spacious ? " event-details-spacious" : ""}`}>
      <p className="par">פרטי המשימה</p>
      <div className="date-details-container">
        <EventDayDate
          date={eventDetails ? getOpportunityScheduleDate(eventDetails) : undefined}
          fallbackLabel={
            eventDetails && !eventDetails.hasTime
              ? formatOpportunityTimeDescription(eventDetails)
              : undefined
          }
          amount={eventDetails?.amount || 1}
          isFull={eventDetails.isFull}
          participants={eventDetails?.participantsCount || 0}
        />
        <div className="details-groups-container">
          <div className="details-container">
            {firstDetailsGroup.map((detail) => (
              <div
                key={detail.key}
                className="detail-item"
                data-tour={tourAnchors ? detail.tourAnchor : undefined}
              >
                {detail.icon}
                <p>{detail.content}</p>
              </div>
            ))}
          </div>
          <div className="details-container">
            {secondDetailsGroup.map((detail) => (
              <div
                key={detail.key}
                className="detail-item"
                data-tour={tourAnchors ? detail.tourAnchor : undefined}
              >
                {detail.icon}
                <p>{detail.content}</p>
              </div>
            ))}
          </div>
          <div className="details-container">
            {ThirdDetailsGroup.map((detail) => (
              <div
                key={detail.key}
                className="detail-item"
                data-tour={tourAnchors ? detail.tourAnchor : undefined}
              >
                {detail.icon}
                {detail.content}
              </div>
            ))}
          </div>
          <Stack alignItems="flex-start" flexDirection="row">
            <Select
              className="status-select-groups"
              labelId="select-label"
              value={eventDetails.progressStatus?.title || "ללא סטטוס"}
              sx={{
                backgroundColor: eventDetails.progressStatus?.color,
                color: eventDetails.progressStatus?.color ? "white" : "black",
                display: "flex",
                textAlign: "center",
                marginBlock: "0.3rem",
                width: "100%",
              }}
              IconComponent={undefined}
            >
              <MenuItem
                sx={{
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                  color: "black",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
                value="ללא סטטוס"
                onClick={async () => {
                  await opportunityRepo.update(eventDetails.id, { progressStatus: null as any });
                  setAlertText("הסטטוס עודכן בהצלחה");
                  setSeverity("success");
                }}
              >
                ללא סטטוס
              </MenuItem>
              {currOrganization?.status
                ?.filter((status) => status.type === "MISSION")
                .sort((a, b) => a.order - b.order)
                .map((status) => (
                  <MenuItem
                    sx={{
                      backgroundColor: `${status.color} !important`,
                      "&:hover": {
                        backgroundColor: `${status.color}CC`,
                      },
                      "&.Mui-selected": {
                        backgroundColor: `${status.color}CC`,
                        border: "3.5px solid white",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: `${status.color}CC`,
                      },
                      color: "white",
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                    }}
                    key={status.id}
                    value={status.title}
                    onClick={async () => {
                      await opportunityRepo.update(eventDetails, {
                        progressStatus: status,
                      });
                      setAlertText("הסטטוס עודכן בהצלחה");
                      setSeverity("success");
                    }}
                  >
                    {status.title}
                  </MenuItem>
                ))}
            </Select>
          </Stack>
        </div>
      </div>
    </div>
  );
};
